import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setOrganizationRoles,
  setOrganizationRolesLoading,
  setOrganizationRolesError,
} from "../redux/actions/organizationRoles";
import { apiV2Service } from "../services/apiV2Service";
import logger from "../utils/logger";

/**
 * Custom hook to manage organization roles data with Redux caching
 * - Loads organization roles only once per session
 * - Caches data in Redux store
 * - Automatically clears on logout
 */
export const useOrganizationRoles = (organizationTypeId = null) => {
  const dispatch = useDispatch();
  const { organizationRoles, organizationRolesLoading, organizationRolesError } = useSelector(state => ({
    organizationRoles: state.organizationRoles?.organizationRoles || [],
    organizationRolesLoading: state.organizationRoles?.loading || false,
    organizationRolesError: state.organizationRoles?.error || null,
  }));

  const loadOrganizationRoles = useCallback(
    async (typeId = organizationTypeId) => {
      logger.log("🔍 loadOrganizationRoles called with:", { typeId, organizationTypeId });

      // If organization roles are already loaded for the current type, don't fetch again
      if (organizationRoles && organizationRoles.length > 0 && !typeId) {
        logger.log("✅ Organization roles already cached in Redux, skipping API call");
        return organizationRoles;
      }

      try {
        logger.log("🔄 Fetching organization roles from API...", { typeId });
        dispatch(setOrganizationRolesLoading(true));
        dispatch(setOrganizationRolesError(null));

        const response = await apiV2Service.getOrganizationRoles({
          active: true,
          limit: 100,
          orderBy: "name:asc",
          organizationTypeId: typeId || undefined,
        });

        logger.log("📦 Organization roles API response:", response);

        // Handle various response structures robustly
        let roles = [];
        if (Array.isArray(response)) {
          roles = response;
        } else if (response && Array.isArray(response.data)) {
          roles = response.data;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          roles = response.data.data;
        }

        logger.log(`✅ Roles extracted: ${roles.length}`);
        dispatch(setOrganizationRoles(roles));
        return roles;
      } catch (error) {
        logger.error("❌ Error loading organization roles:", error);
        dispatch(setOrganizationRolesError(error.message || "Failed to load organization roles"));
        return [];
      } finally {
        dispatch(setOrganizationRolesLoading(false));
      }
    },
    [organizationRoles, organizationTypeId, dispatch]
  );

  // Auto-load organization roles when hook is first used
  useEffect(() => {
    logger.log("🎯 useOrganizationRoles useEffect checking...", {
      count: organizationRoles?.length,
      loading: organizationRolesLoading,
      error: organizationRolesError,
    });

    if (organizationRoles && organizationRoles.length === 0 && !organizationRolesLoading && !organizationRolesError) {
      logger.log("🎯 useOrganizationRoles: No cached roles found, triggering load...");
      loadOrganizationRoles();
    }
  }, [organizationRoles, organizationRolesLoading, organizationRolesError, loadOrganizationRoles]);

  return {
    organizationRoles,
    organizationRolesLoading,
    organizationRolesError,
    loadOrganizationRoles,
    hasOrganizationRoles: organizationRoles && organizationRoles.length > 0,
  };
};
